import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Fab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  CalendarToday as DateIcon,
  Assignment as InspectionIcon,
  TrendingUp as TrendIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import { fetchInspections, deleteInspection } from '@store/slices/inspectionsSlice'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { Inspection } from '@types'
import { format, parseISO } from 'date-fns'

const HiveInspections: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()

  const { inspections, loadingState: inspectionsLoading, error } = useAppSelector((state) => state.inspections)
  const { hives, loadingState: locationsLoading } = useAppSelector((state) => state.locations)

  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null)

  const hiveId = parseInt(id || '0')
  const hive = hives.find((h) => h.id === hiveId)

  useEffect(() => {
    if (hiveId) {
      dispatch(fetchInspections({ hiveId }))
    }
  }, [dispatch, hiveId])

  useEffect(() => {
    if (!hive && locationsLoading === 'idle') {
      dispatch(fetchApiariesAndGroups())
    }
  }, [dispatch, hive, locationsLoading])

  const inspectionDateValue = (inspection: Inspection) =>
    inspection.date || (inspection as { inspection_date?: string }).inspection_date

  const formatInspectionDate = (value?: string) => {
    if (!value) return 'Unknown date'
    try {
      return format(parseISO(value), 'PPP')
    } catch {
      return value
    }
  }

  // Filter inspections for this hive
  const hiveInspections = inspections.filter(
    (inspection) => inspection.hive_id === hiveId || inspection.hive?.id === hiveId,
  )

  // Apply additional filters
  const filteredInspections = hiveInspections.filter((inspection) => {
    const dateValue = inspectionDateValue(inspection)
    if (search && !inspection.notes?.toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    if (startDate && dateValue && new Date(dateValue) < startDate) {
      return false
    }
    if (endDate && dateValue && new Date(dateValue) > endDate) {
      return false
    }
    return true
  })

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = () => {
    setSearch('')
  }

  const handleNewInspection = () => {
    navigate(`/inspections/create?hiveId=${hiveId}`)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, inspection: Inspection) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedInspection(inspection)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedInspection(null)
  }

  const handleEditInspection = () => {
    if (selectedInspection) {
      navigate(`/hives/${hiveId}/inspections/${selectedInspection.id}`)
    }
    handleMenuClose()
  }

  const handleDeleteInspection = async () => {
    if (selectedInspection && window.confirm('Are you sure you want to delete this inspection?')) {
      await dispatch(deleteInspection(selectedInspection.id))
    }
    handleMenuClose()
  }

  const handlePrintInspection = () => {
    if (selectedInspection) {
      window.print()
    }
    handleMenuClose()
  }

  const handleInspectionClick = (inspection: Inspection) => {
    navigate(`/hives/${hiveId}/inspections/${inspection.id}`)
  }

  const handleViewData = () => {
    navigate(`/measurements/${hiveId}`)
  }

  const getImpressionColor = (impression: number) => {
    if (impression >= 3) return 'success'
    if (impression === 2) return 'warning'
    if (impression === 1) return 'error'
    return 'default'
  }

  const getImpressionLabel = (impression: number) => {
    if (impression >= 4) return 'Good'
    if (impression === 3) return 'Fair'
    if (impression === 2) return 'Needs work'
    if (impression === 1) return 'Poor'
    return 'Unknown'
  }

  if ((locationsLoading === 'pending' || locationsLoading === 'idle') && !hive) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  if (!hive) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography variant="h4" color="error" align="center">
          Hive not found
        </Typography>
      </Container>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {hive.name} - Inspections
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => navigate(`/hives/${hiveId}`)}
            >
              Hive overview
            </Button>
            <Button
              variant="outlined"
              startIcon={<TrendIcon />}
              onClick={handleViewData}
            >
              View Data
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => navigate('/')}
            >
              Back to Hives
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search inspections..."
                  value={search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Inspections List */}
        {filteredInspections.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <InspectionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No inspections found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {hiveInspections.length === 0
                  ? "Start by creating your first inspection for this hive"
                  : "Try adjusting your search filters"
                }
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {filteredInspections.map((inspection) => (
              <Grid item xs={12} key={inspection.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-1px)',
                    },
                  }}
                  onClick={() => handleInspectionClick(inspection)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <DateIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            {formatInspectionDate(inspectionDateValue(inspection))}
                          </Typography>
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          Inspection #{inspection.id}
                        </Typography>

                        {inspection.notes && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              mb: 2,
                            }}
                          >
                            {inspection.notes}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Impression: ${getImpressionLabel(inspection.impression)}`}
                            size="small"
                            color={getImpressionColor(inspection.impression) as any}
                            variant="outlined"
                          />
                          {inspection.attention && (
                            <Chip
                              label="Needs Attention"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {inspection.reminder && (
                            <Chip
                              label="Has Reminder"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>

                      {inspection.owner && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, inspection)}
                        >
                          <MoreIcon />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: isMobile ? 80 : 24,
            right: 24,
          }}
          onClick={handleNewInspection}
        >
          <AddIcon />
        </Fab>

        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEditInspection}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Inspection</ListItemText>
          </MenuItem>

          <MenuItem onClick={handlePrintInspection}>
            <ListItemIcon>
              <PrintIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Print</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleDeleteInspection} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Container>
    </LocalizationProvider>
  )
}

export default HiveInspections
